import * as Sentry from '@sentry/react';
import { Box, Flex } from '@televet/kibble-ui';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import { useGetAppointmentRecapUrlLazyQuery } from 'shared/types/graphql';
import { audioStringToMp3Blob } from 'shared/utils/fileDownloader';
import { formatDuration } from 'shared/utils/formatTime';

type AudioPlayerProps = {
  recapId: string;
};

const AudioPlayer = ({ recapId }: AudioPlayerProps): JSX.Element => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const toast = useToast();

  const [getUrl] = useGetAppointmentRecapUrlLazyQuery();

  const handleLoadAndPlay = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const recapData = await getUrl({
        variables: { data: { recapId } },
        fetchPolicy: GraphQLFetchPolicies.CacheFirst,
      });

      const mp3Base64 = recapData?.data?.getAppointmentRecapUrl;

      if (!mp3Base64) {
        throw new Error('No audio data found recapId: ' + recapId);
      }

      const blob = audioStringToMp3Blob(mp3Base64);
      const url = URL.createObjectURL(blob);

      const audio = new Audio(url);
      audioRef.current = audio;

      // When metadata loads, set the audio duration
      audio.addEventListener('loadedmetadata', () => {
        if (audioRef.current) setDuration(audioRef.current.duration);
      });

      // Update currentTime on each tick
      audio.addEventListener('timeupdate', () => {
        if (audioRef.current) setCurrentTime(audioRef.current.currentTime);
      });

      audio.addEventListener('ended', () => setIsPlaying(false));

      await audio.play();
      setIsPlaying(true);
    } catch (err) {
      Sentry.captureException(err);
      toast({
        title: 'Error playing audio',
        description: 'We could not load the audio for this recap. Please try again later.',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [getUrl, recapId, toast]);

  const handlePlayPause = useCallback(async (): Promise<void> => {
    if (!audioRef.current) return handleLoadAndPlay();
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      await audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  }, [handleLoadAndPlay, isPlaying]);

  const handleSeek = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    const t = Number(e.target.value);
    if (audioRef.current) audioRef.current.currentTime = t;
    setCurrentTime(t);
  }, []);

  // Stop audio and clean up when component unmounts
  useEffect(() => {
    return (): void => {
      if (audioRef.current) {
        audioRef.current.pause();
        URL.revokeObjectURL(audioRef.current.src);
      }
    };
  }, []);

  return (
    <Box width="100%">
      <Flex gap={2} mb={2} justifyContent="center">
        <Button
          size="sm"
          iconName={isPlaying ? 'pause' : 'play'}
          onClick={handlePlayPause}
          isLoading={loading}
          variant="tertiary"
        />
        <Box width="100%" pt={'6px'}>
          <input
            type="range"
            min={0}
            max={duration}
            value={currentTime}
            onChange={handleSeek}
            style={{ width: '100%' }}
            disabled={!audioRef.current}
          />

          <Box textAlign="right" fontSize="xs">
            {formatDuration(currentTime)} / {formatDuration(duration)}
          </Box>
        </Box>
      </Flex>
    </Box>
  );
};

export default AudioPlayer;
